import { PdfDownloadButton } from '../components/PdfDownloadButton';
import { InvoiceDetailLines } from '../components/InvoiceLines';
import type { InvoiceLineRow } from '../../shared/types';
import { InvoiceKindChip, InvoiceKindSelect } from '../components/InvoiceKind';
// Invoice detail per mockups/06-invoice-detail.html: breadcrumb with status
// pill + actions (reminder / void / record payment), amount-due banner
// (danger chrome while overdue), autosaving notes, timeline from the
// activity log, and the money/dates/contact rail. The contact fields
// override per invoice and inherit from the client when null (same pattern
// as project detail). "Send reminder" logs a timeline entry only — Henry
// sends the actual email from Gmail. "Copy public link" / "Send invoice"
// (Wave C2) call the new public-link + email-send routes — the public link
// always works; "Send invoice" surfaces a friendly message when email
// isn't configured (RESEND_API_KEY/EMAIL_FROM unset) rather than failing
// silently.

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import { invoiceNumberWarning } from '../lib/invoiceNumbers';
import { linkLabel } from '../lib/invoiceDetailShared';
import { useIsMobile } from '../lib/useIsMobile';
import { melbourneDate, melbourneToday } from '../../shared/dates';
import { displayCents, useMoneyMode } from '../lib/money';
import { grossFromEx, gstOnEx } from '../../shared/finance';
import {
  INVOICE_STATUSES,
  type Brand,
  type ClientWithCounts,
  type InvoiceRow,
  type ProjectWithClient,
} from '../../shared/types';
import {
  ContactLine,
  Dropdown,
  formatDate,
  formatInvoiceNumber,
  formatMoneyExact,
  formatProjectNumber,
  Group,
  InlineDate,
  InlineMoney,
  InlineText,
  MetaStamp,
  Modal,
  NotesBlock,
  Pill,
  Seg,
  Timeline,
  TimelineRow,
  TrashAction,
} from '../components/primitives';
import { FieldSheet } from '../components/FieldSheet';
import { InvoiceDetailMobile } from '../components/InvoiceDetailMobile';
import { displayStatus, INVOICE_STATUS_LABELS, invoiceStatusTone } from './Finance';

interface InvoiceDetailRow extends InvoiceRow {
  lines: InvoiceLineRow[];
  client_name: string;
  client_contact_name: string | null;
  client_contact_email: string | null;
  client_contact_phone: string | null;
  project_name: string | null;
  project_number: number | null;
  project_brand: Brand | null;
}

interface ActivityEntry {
  id: number;
  kind: string;
  summary: string;
  created_at: number;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const NO_PROJECT_VALUE = '__no_project__';

function tlDate(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]}`;
}

function InvoiceNumberEditor({
  invoice,
  onSaved,
  mobile = false,
  openSignal = 0,
}: {
  invoice: InvoiceDetailRow;
  onSaved: (invoiceNumber: string) => void;
  /** ≤880px: the editor renders nothing until the title's number (or the
      "···" menu) bumps openSignal, then it is a field sheet with the same
      warn-only duplicate gate, confirmed in a half sheet (§8.2 F). */
  mobile?: boolean;
  /** Mobile only — bump to open the field sheet. */
  openSignal?: number;
}) {
  const [editing, setEditing] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Warn-only duplicate gate: the number this warning was raised for. Saving
  // the same number again is the explicit "save anyway" step.
  const [duplicateOf, setDuplicateOf] = useState<{ number: string; warning: string } | null>(null);

  const open = () => {
    setDraft(invoice.invoice_number);
    setError(null);
    setDuplicateOf(null);
    setEditing(true);
  };
  // Mobile: the title's number bumps openSignal instead of clicking a span.
  useEffect(() => {
    if (!mobile || openSignal <= 0) return;
    setDraft(invoice.invoice_number);
    setError(null);
    setDuplicateOf(null);
    setSheetOpen(true);
    // Only the signal opens the sheet; the seed is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal, mobile]);

  const save = async (raw: string = draft) => {
    if (saving) return;
    const invoiceNumber = raw.trim();
    if (!invoiceNumber) {
      const message = 'Invoice number is required.';
      setError(message);
      // The mobile FieldSheet has already closed by the time we get here and
      // the inline error only renders in the duplicate sheet, so use the
      // app toast (review C-4).
      if (mobile) reportError(new Error(message), message);
      return;
    }
    if (invoiceNumber === invoice.invoice_number) {
      setEditing(false);
      setError(null);
      setDuplicateOf(null);
      return;
    }
    setDraft(invoiceNumber);
    if (duplicateOf?.number !== invoiceNumber) {
      const warning = await invoiceNumberWarning(invoiceNumber, invoice.id);
      if (warning) {
        setDuplicateOf({ number: invoiceNumber, warning });
        setError(null);
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      await api(`/api/admin/finance/invoices/${invoice.id}`, {
        method: 'PATCH',
        json: { invoice_number: invoiceNumber },
      });
      onSaved(invoiceNumber);
      setEditing(false);
      setDuplicateOf(null);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to update invoice number.');
      // The mobile sheet closes on Save, so its errors need the app toast.
      if (mobile) reportError(saveError, 'Failed to update invoice number.');
    } finally {
      setSaving(false);
    }
  };

  // ≤880px: the field sheet, with the duplicate warning promoted to its own
  // half sheet — the same warn-only gate, one tap to "Save anyway →".
  if (mobile) {
    return (
      <>
        {sheetOpen && (
          <FieldSheet
            open
            title="Invoice number"
            context={`Now ${formatInvoiceNumber(invoice.invoice_number)}`}
            kind="text"
            value={draft}
            placeholder="244"
            onSave={(raw) => void save(raw)}
            onClose={() => setSheetOpen(false)}
          />
        )}
        {duplicateOf &&
          createPortal(
            <Modal
              title="Number already used"
              context="INVOICES"
              width={460}
              mobile="half"
              onClose={() => {
                if (saving) return;
                setDuplicateOf(null);
                setError(null);
              }}
              hint="Invoice numbers don't have to be unique"
              foot={
                <>
                  <button className="btn" onClick={() => setDuplicateOf(null)} disabled={saving}>
                    Cancel
                  </button>
                  <button className="btn primary" onClick={() => void save(duplicateOf.number)} disabled={saving}>
                    {saving ? 'Saving…' : 'Save anyway →'}
                  </button>
                </>
              }
            >
              <p className="renumber-intro">{duplicateOf.warning}</p>
              {error && <p className="error-text">{error}</p>}
            </Modal>,
            document.body,
          )}
      </>
    );
  }

  if (!editing) {
    return (
      <span className="editable mono" onClick={open} title="Edit invoice number">
        {formatInvoiceNumber(invoice.invoice_number)}
      </span>
    );
  }

  return (
    // Positioned wrapper so the duplicate warning hangs under the field
    // instead of stretching the breadcrumb row it lives in.
    <span className="number-edit">
      <input
        className="inline-input mono invoice-number-input"
        type="text"
        value={draft}
        disabled={saving}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={(event) => {
          if (event.currentTarget.dataset.cancelSave !== 'true') void save();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') {
            event.currentTarget.dataset.cancelSave = 'true';
            setEditing(false);
            setError(null);
            setDuplicateOf(null);
          }
        }}
        autoFocus
      />
      {error && <span className="error-text project-number-error">{error}</span>}
      {duplicateOf?.number === draft.trim() && (
        <span className="number-warning">
          {duplicateOf.warning}{' '}
          <button className="link-btn" onClick={() => void save()} disabled={saving}>
            Save anyway
          </button>
        </span>
      )}
    </span>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<InvoiceDetailRow | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [missing, setMissing] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [clients, setClients] = useState<ClientWithCounts[] | null>(null);
  const [projects, setProjects] = useState<ProjectWithClient[] | null>(null);
  const [reminderBusy, setReminderBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkNote, setLinkNote] = useState<string | null>(null);
  const [sendBusy, setSendBusy] = useState(false);
  const [sendNote, setSendNote] = useState<string | null>(null);
  // ≤880px: the title's number bumps this instead of clicking a header span.
  const [numberSignal, setNumberSignal] = useState(0);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const today = melbourneToday();
  const mode = useMoneyMode();

  const refresh = useCallback(() => {
    api<{ invoice: InvoiceDetailRow; activity: ActivityEntry[] }>(`/api/admin/finance/invoices/${id}`)
      .then((d) => {
        setInvoice(d.invoice);
        setActivity(d.activity);
      })
      .catch(() => setMissing(true));
  }, [id]);
  useEffect(refresh, [refresh]);

  const patch = async (fields: Record<string, unknown>) => {
    if (!invoice) return;
    const prev = invoice;
    setInvoice({ ...invoice, ...fields } as InvoiceDetailRow);
    try {
      await api(`/api/admin/finance/invoices/${invoice.id}`, { method: 'PATCH', json: fields });
      refresh();
    } catch (error) {
      setInvoice(prev);
      reportError(error, 'Could not save the invoice change.');
    }
  };

  // The two lazy loads, shared with the mobile pickers (which have no
  // open/close toggle of their own — the sheet is the toggle).
  const ensureClients = useCallback(() => {
    if (clients !== null) return;
    setClients([]);
    api<{ clients: ClientWithCounts[] }>('/api/admin/clients')
      .then((data) => setClients(data.clients))
      .catch((error) => {
        setClients(null);
        reportError(error, 'Could not load clients.');
      });
  }, [clients]);

  const ensureProjects = useCallback(() => {
    if (projects !== null) return;
    setProjects([]);
    api<{ projects: ProjectWithClient[] }>('/api/admin/projects')
      .then((data) => setProjects(data.projects.filter((project) => project.voided_at === null)))
      .catch((error) => {
        setProjects(null);
        reportError(error, 'Could not load projects.');
      });
  }, [projects]);

  const openClientPicker = () => {
    if (invoice?.project_id) return;
    setProjectOpen(false);
    const next = !clientOpen;
    setClientOpen(next);
    if (next) ensureClients();
  };

  const openProjectPicker = () => {
    setClientOpen(false);
    const next = !projectOpen;
    setProjectOpen(next);
    if (next) ensureProjects();
  };

  const sendReminder = async () => {
    if (!invoice || reminderBusy) return;
    setReminderBusy(true);
    try {
      await api(`/api/admin/finance/invoices/${invoice.id}/reminder`, { method: 'POST' });
      refresh();
    } finally {
      setReminderBusy(false);
    }
  };

  const copyPublicLink = async () => {
    if (!invoice || linkBusy) return;
    setLinkBusy(true);
    setLinkNote(null);
    try {
      const result = await api<{ url: string }>(`/api/admin/finance/invoices/${invoice.id}/public-link`, {
        method: 'POST',
      });
      await navigator.clipboard.writeText(result.url);
      setLinkNote('Copied!');
    } catch (err) {
      setLinkNote(err instanceof Error ? err.message : 'Failed to create link.');
    } finally {
      setLinkBusy(false);
      setTimeout(() => setLinkNote(null), 4000);
    }
  };

  const sendInvoiceEmail = async () => {
    if (!invoice || sendBusy) return;
    setSendBusy(true);
    setSendNote(null);
    try {
      await api(`/api/admin/finance/invoices/${invoice.id}/send`, { method: 'POST' });
      setSendNote('Sent!');
      refresh();
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setSendNote('Email not configured — copy the public link instead.');
      } else {
        setSendNote(err instanceof Error ? err.message : 'Failed to send.');
      }
    } finally {
      setSendBusy(false);
      setTimeout(() => setSendNote(null), 6000);
    }
  };

  if (missing) {
    return (
      <div className="page-body">
        <div className="content">
          <div className="empty-note">Invoice not found.</div>
        </div>
      </div>
    );
  }
  if (!invoice) return <div className="page-body" />;

  const status = displayStatus(invoice, today);
  const number = formatInvoiceNumber(invoice.invoice_number);
  const overdueDays =
    status === 'overdue' && invoice.due_date
      ? Math.max(1, Math.round((Date.parse(today) - Date.parse(invoice.due_date)) / 86400000))
      : null;
  const actionable = status === 'draft' || status === 'sent' || status === 'overdue';
  const effectiveBrand = invoice.project_brand ?? invoice.brand;

  const subBits = [invoice.client_name, invoice.project_name].filter(Boolean) as string[];

  /* ≤880px (Phase C §8.2): the whole page is the mobile component; the
     two-column .detail-grid and the rail are not rendered. Every handler
     below is shared with the desktop render. */
  const numberEditor = (mobile: boolean) => (
    <InvoiceNumberEditor
      invoice={invoice}
      onSaved={(invoiceNumber) => {
        setInvoice({ ...invoice, invoice_number: invoiceNumber });
        refresh();
      }}
      mobile={mobile}
      openSignal={numberSignal}
    />
  );

  if (isMobile) {
    return (
      <>
        <InvoiceDetailMobile
          invoice={invoice}
          linesSection={<InvoiceDetailLines invoice={invoice} onSaved={refresh} />}
          status={status}
          statusLabel={INVOICE_STATUS_LABELS[status]}
          statusTone={invoiceStatusTone(status)}
          statusItems={INVOICE_STATUSES.map((s) => ({ value: s, label: INVOICE_STATUS_LABELS[s] }))}
          mode={mode}
          number={number}
          effectiveBrand={effectiveBrand}
          overdueDays={overdueDays}
          subBits={subBits}
          activity={activity}
          tlDate={tlDate}
          clients={clients}
          ensureClients={ensureClients}
          projects={projects}
          ensureProjects={ensureProjects}
          patch={patch}
          reminderBusy={reminderBusy}
          linkBusy={linkBusy}
          sendBusy={sendBusy}
          actionNote={linkNote ?? sendNote}
          sendReminder={sendReminder}
          copyPublicLink={copyPublicLink}
          sendInvoiceEmail={sendInvoiceEmail}
          onOpenNumber={() => setNumberSignal((n) => n + 1)}
          onTrash={async () => {
            await api(`/api/admin/finance/invoices/${invoice.id}`, { method: 'DELETE' });
            navigate('/finance');
          }}
        />
        {numberEditor(true)}
      </>
    );
  }

  return (
    <>
      <header className="detail-header">
        <Link className="crumb" to="/finance">
          Finance
        </Link>
        <span className="crumb-sep">/</span>
        <span className="crumb-current">
          Invoice {numberEditor(false)}
        </span>
        <span style={{ marginLeft: 6 }}>
          <Pill tone={invoiceStatusTone(status)}>{INVOICE_STATUS_LABELS[status]}</Pill>
        </span>
        <InvoiceKindChip kind={invoice.invoice_kind} />
        <span style={{ marginLeft: 6 }}>
          <Pill tone={effectiveBrand === 'hsp' ? 'accent' : undefined}>{effectiveBrand}</Pill>
        </span>
        <div className="detail-actions">
          {(linkNote || sendNote) && (
            <span style={{ fontSize: 12, color: 'var(--text-dim)', alignSelf: 'center' }}>
              {linkNote ?? sendNote}
            </span>
          )}
          {status !== 'void' && (
            <button className="btn" onClick={copyPublicLink} disabled={linkBusy}>
              {linkBusy ? 'Creating…' : 'Copy public link'}
            </button>
          )}
          {actionable && (
            <button className="btn" onClick={sendInvoiceEmail} disabled={sendBusy}>
              {sendBusy ? 'Sending…' : 'Send invoice'}
            </button>
          )}
          {(status === 'sent' || status === 'overdue') && (
            <button className="btn" onClick={sendReminder} disabled={reminderBusy}>
              {reminderBusy ? 'Logging…' : 'Send reminder'}
            </button>
          )}
          {actionable && (
            <button className="btn" onClick={() => patch({ status: 'void' })}>
              Void
            </button>
          )}
          {actionable && (
            <button className="btn primary" onClick={() => patch({ status: 'paid' })}>
              Record payment →
            </button>
          )}
        </div>
      </header>

      <div className="page-body">
        <div className="detail-grid">
          <div className="detail-main">
            <h1 className="detail-name">Invoice {number}</h1>
            <div className="detail-sub">{subBits.join(' · ')}</div>

            <Group title="Public description" style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 6 }}>
                Shown to the client on the public invoice page — separate from the notes below, which stay internal.
              </div>
              <InlineText
                value={invoice.description}
                placeholder="e.g. Deposit — 2-day shoot package"
                onSave={(description) => patch({ description })}
              />
            </Group>
            <InvoiceDetailLines invoice={invoice} onSaved={refresh} />

            <div className={`amount-card${status === 'overdue' ? ' overdue' : ''}`}>
              <div>
                <span className="lbl">
                  {status === 'paid' ? 'Amount' : 'Amount due'}
                  {mode === 'ex' ? ' · ex GST' : ''}
                </span>
                <span className="big">
                  {formatMoneyExact(displayCents(invoice.amount_cents, invoice.gst_cents, mode))}
                </span>
              </div>
              <div>
                <span className="lbl">{mode === 'ex' ? '+ GST' : 'Incl. GST'}</span>
                <span className="val">{formatMoneyExact(invoice.gst_cents)}</span>
              </div>
              {mode === 'ex' && (
                <div>
                  <span className="lbl">Total incl. GST</span>
                  <span className="val">{formatMoneyExact(invoice.amount_cents)}</span>
                </div>
              )}
              <div>
                <span className="lbl">Due date</span>
                <span className="val">{formatDate(invoice.due_date)}</span>
              </div>
              {status === 'paid' && invoice.paid_date && (
                <div>
                  <span className="lbl">Paid</span>
                  <span className="val">{formatDate(invoice.paid_date)}</span>
                </div>
              )}
              {overdueDays !== null && (
                <span className="od">⚠ {overdueDays} day{overdueDays === 1 ? '' : 's'} overdue</span>
              )}
            </div>

            <NotesBlock notes={invoice.notes} onSave={(notes) => patch({ notes })} />

            <Group title="Timeline">
              <Timeline>
                {activity.map((entry) => (
                  <TimelineRow key={entry.id} date={tlDate(entry.created_at)}>
                    {entry.summary}
                  </TimelineRow>
                ))}
                {!activity.some((a) => a.kind === 'invoice.created') && (
                  <TimelineRow date={tlDate(invoice.created_at)}>
                    Invoice {number} created
                    {invoice.project_name && (
                      <>
                        {' '}
                        for <span className="hl">{invoice.project_name}</span>
                      </>
                    )}
                  </TimelineRow>
                )}
              </Timeline>
            </Group>
          </div>

          <div className="detail-rail">
            <div className="field"><div className="field-label">Kind</div><InvoiceKindSelect value={invoice.invoice_kind} onChange={invoice_kind => void patch({ invoice_kind })} /></div>
            <div className="field">
              <div className="field-label">Status</div>
              <div className="dropdown-anchor">
                <span className="select-pill" onClick={() => setStatusOpen((o) => !o)}>
                  <Pill tone={invoiceStatusTone(status)}>{INVOICE_STATUS_LABELS[status]}</Pill>
                  <span className="caret">▾</span>
                </span>
                {statusOpen && (
                  <Dropdown
                    items={INVOICE_STATUSES.map((s) => ({ value: s, label: INVOICE_STATUS_LABELS[s] }))}
                    value={invoice.status}
                    onSelect={(status) => patch({ status })}
                    onClose={() => setStatusOpen(false)}
                  />
                )}
              </div>
            </div>
            <div className="field">
              <div className="field-label">Brand</div>
              {invoice.project_id ? (
                <div className="field-value">
                  <Pill tone={effectiveBrand === 'hsp' ? 'accent' : undefined}>{effectiveBrand}</Pill>
                  <span className="dim" style={{ marginLeft: 7, fontSize: 10 }}>project</span>
                </div>
              ) : (
                <Seg<Brand>
                  options={[
                    { value: 'hsp', label: 'HSP' },
                    { value: 'ss', label: 'SS' },
                  ]}
                  value={invoice.brand}
                  onChange={(brand) => patch({ brand })}
                />
              )}
            </div>
            <div className="field">
              <div className="field-label">{mode === 'ex' ? 'Amount · ex GST' : 'Amount'}</div>
              <div style={{ fontSize: 15 }}>
                {invoice.lines?.length ? <span className="mono">{formatMoneyExact(displayCents(invoice.amount_cents, invoice.gst_cents, mode))}</span> : <InlineMoney
                  cents={displayCents(invoice.amount_cents, invoice.gst_cents, mode)}
                  exact
                  onSave={(cents) => {
                    if (cents === null) return;
                    const gstFree = invoice.gst_cents === 0;
                    if (mode === 'ex' && !gstFree) {
                      // Entered ex GST — store gross + the 10% component.
                      patch({ amount_cents: grossFromEx(cents), gst_cents: gstOnEx(cents) });
                    } else if (gstFree) {
                      patch({ amount_cents: cents, gst_cents: 0 });
                    } else {
                      patch({ amount_cents: cents });
                    }
                  }}
                />}
              </div>
            </div>
            <div className="field">
              <div className="field-label">GST</div>
              {invoice.lines?.length ? <span className="mono">{formatMoneyExact(invoice.gst_cents)}</span> : <InlineMoney
                cents={invoice.gst_cents}
                exact
                valueClass="mono"
                onSave={(gst_cents) => gst_cents !== null && patch({ gst_cents })}
              />}
            </div>
            <div className="field">
              <div className="field-label">Issued</div>
              <InlineDate value={invoice.issue_date} onSave={(issue_date) => patch({ issue_date })} />
            </div>
            <div className="field">
              <div className="field-label">Due</div>
              <InlineDate
                value={invoice.due_date}
                style={status === 'overdue' ? { color: 'var(--danger)' } : undefined}
                onSave={(due_date) => patch({ due_date })}
              />
            </div>
            {invoice.paid_date && (
              <div className="field">
                <div className="field-label">Paid</div>
                <InlineDate value={invoice.paid_date} onSave={(paid_date) => patch({ paid_date })} allowClear />
              </div>
            )}
            <div className="field">
              <div className="field-label">Client</div>
              <div className="field-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="dropdown-anchor">
                  <span
                    className={`select-pill${invoice.project_id ? ' dim' : ''}`}
                    onClick={invoice.project_id ? undefined : openClientPicker}
                    style={invoice.project_id ? { cursor: 'default' } : undefined}
                  >
                    {invoice.client_name}
                    {!invoice.project_id && <span className="caret">▾</span>}
                  </span>
                  {clientOpen && !invoice.project_id && (
                    <Dropdown
                      items={(clients ?? []).map((client) => ({ value: client.id, label: client.name }))}
                      value={invoice.client_id}
                      onSelect={(client_id) => {
                        if (client_id !== invoice.client_id) void patch({ client_id });
                      }}
                      onClose={() => setClientOpen(false)}
                      searchable
                    />
                  )}
                </div>
                <Link to={`/clients/${invoice.client_id}`} style={{ fontSize: 10 }}>open ↗</Link>
              </div>
              {invoice.project_id && (
                <div className="field-value dim" style={{ marginTop: 4, fontSize: 10 }}>
                  follows the project — change it there or relink below
                </div>
              )}
            </div>
            <div className="field">
              <div className="field-label">Project</div>
              <div className="field-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="dropdown-anchor">
                  <span className="select-pill" onClick={openProjectPicker}>
                    {invoice.project_name ?? '— No project'}
                    <span className="caret">▾</span>
                  </span>
                  {projectOpen && (
                    <Dropdown
                      items={[
                        { value: NO_PROJECT_VALUE, label: '— No project' },
                        ...(projects ?? []).map((project) => ({
                          value: project.id,
                          label: [formatProjectNumber(project.project_number), project.name, project.client_name]
                            .filter(Boolean)
                            .join(' · '),
                        })),
                      ]}
                      value={invoice.project_id ?? NO_PROJECT_VALUE}
                      onSelect={(project_id) => {
                        if (project_id === NO_PROJECT_VALUE) {
                          if (invoice.project_id) void patch({ project_id: null });
                          return;
                        }
                        const project = projects?.find((candidate) => candidate.id === project_id);
                        if (project && project.id !== invoice.project_id) {
                          setClientOpen(false);
                          void patch({ project_id: project.id, client_id: project.client_id });
                        }
                      }}
                      onClose={() => setProjectOpen(false)}
                      searchable
                    />
                  )}
                </div>
                {invoice.project_id && (
                  <Link to={`/projects/${invoice.project_id}`} style={{ fontSize: 10 }}>open ↗</Link>
                )}
              </div>
            </div>
            <div className="field">
              <div className="field-label">Contact</div>
              <ContactLine
                value={invoice.contact_name}
                fallback={invoice.client_contact_name}
                onSave={(contact_name) => patch({ contact_name })}
              />
              <ContactLine
                value={invoice.contact_email}
                fallback={invoice.client_contact_email}
                mono
                accent
                onSave={(contact_email) => patch({ contact_email })}
              />
              <ContactLine
                value={invoice.contact_phone}
                fallback={invoice.client_contact_phone}
                mono
                onSave={(contact_phone) => patch({ contact_phone })}
              />
            </div>
            <div className="field">
              <div className="field-label">External link</div>
              <InlineText
                value={invoice.external_url}
                mono
                valueClass="mono"
                placeholder="https://…"
                display={
                  invoice.external_url ? (
                    <a href={invoice.external_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                      {linkLabel(invoice.external_url)} ↗
                    </a>
                  ) : undefined
                }
                onSave={(external_url) => patch({ external_url })}
              />
            </div>
            <div className="field">
              <div className="field-label">PDF</div>
              <PdfDownloadButton href={`/api/admin/finance/invoices/${encodeURIComponent(invoice.id)}/pdf`} />
            </div>
            <MetaStamp>
              CREATED {formatDate(melbourneDate(invoice.created_at)).toUpperCase()}
              <br />
              UPDATED {formatDate(melbourneDate(invoice.updated_at)).toUpperCase()}
              {invoice.sent_at && (
                <>
                  <br />
                  EMAILED {formatDate(melbourneDate(invoice.sent_at)).toUpperCase()}
                </>
              )}
              {invoice.last_reminder_at && (
                <>
                  <br />
                  LAST REMINDER {formatDate(melbourneDate(invoice.last_reminder_at)).toUpperCase()}
                </>
              )}
            </MetaStamp>
            <TrashAction
              label="Move invoice to trash"
              onConfirm={async () => {
                await api(`/api/admin/finance/invoices/${invoice.id}`, { method: 'DELETE' });
                navigate('/finance');
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
